import { useMutationState } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { getSessionDataConfig } from '../config';
import { DELETE_TRANSCRIPT } from '../mutation/useMutateDeleteTranscript';
import { useQuerySessionById } from '../query/useQuerySessionById';
import { useIsTranscriptDeleted } from '../transcript-deletion/use-is-transcript-deleted';
import {
  CLEAR_DEVICE_SIGNALS,
  type ClientTranscriptDeviceSignals,
  type ClientTranscriptGuardInput,
  type ClientTranscriptGuardReason,
  resolveClientTranscriptGuard,
} from './resolve-client-transcript-guard';

type RustStopSignal = NonNullable<ClientTranscriptGuardInput['rustStop']>;

const rustStops = new Map<string, RustStopSignal>();
const rustStopListeners = new Set<() => void>();

const subscribeRustStops = (listener: () => void) => {
  rustStopListeners.add(listener);
  return () => {
    rustStopListeners.delete(listener);
  };
};

const getServerRustStop = (): RustStopSignal | null => null;

/** Records the latest rust stop for a session, pause stops included; the next stop overwrites it. */
export function rememberClientRustStop(sessionId: string, signal: RustStopSignal): void {
  if (!sessionId) return;
  rustStops.set(sessionId, signal);
  rustStopListeners.forEach((listener) => listener());
}

/** Test-only. */
export function __resetClientRustStopsForTests(): void {
  rustStops.clear();
  rustStopListeners.forEach((listener) => listener());
}

// Module-scope so the unwired fallback is one stable function, always called.
const readClearDeviceSignals = (): ClientTranscriptDeviceSignals => CLEAR_DEVICE_SIGNALS;

/** Why this session's "transcript is safe" claim must stay hidden, or null. */
export function useClientTranscriptGuard(
  sessionId: string
): ClientTranscriptGuardReason | null {
  const { data: backupUploadingStatus } = useQuerySessionById({
    sessionId,
    select: (session) => session?.backupUploadingStatus,
  });
  const transcriptDeleted = useIsTranscriptDeleted(sessionId);
  const deletingSessionIds = useMutationState({
    filters: { mutationKey: [DELETE_TRANSCRIPT], status: 'pending' },
    select: (mutation) =>
      (mutation.state.variables as { sessionId?: string } | undefined)?.sessionId,
  });
  const useDeviceSignals =
    getSessionDataConfig().useClientTranscriptDeviceSignals ?? readClearDeviceSignals;
  const deviceSignals = useDeviceSignals(sessionId);
  const rustStop = useSyncExternalStore(
    subscribeRustStops,
    () => rustStops.get(sessionId) ?? null,
    getServerRustStop
  );

  return resolveClientTranscriptGuard({
    ...deviceSignals,
    backupUploadingStatus,
    rustStop,
    transcriptDeleteInProgress: deletingSessionIds.includes(sessionId),
    transcriptDeleted,
  });
}
